import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  StyleSheet,
  View,
  SectionList,
  Platform,
  Share,
  FlatList,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { activateKeepAwakeAsync, deactivateKeepAwake } from "expo-keep-awake";
import { useFirebase } from "../firebase/useFirebase";
import { useAuth } from "../auth/useAuth";
import { getMyMedia, createMedia, deleteMedia } from "@/services/firebase/media-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { extractMediaMetadata } from "@/services/media-metadata";
import { getMyGroups } from "@/services/firebase/group-functions";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import {
  MediaModel,
  ShareModel,
  ShareSummary,
  Contact,
  GroupModel,
  getMediaType,
  formatMediaCategory,
  determineMediaCategory,
  isPdfMedia,
  isDocumentMedia,
  DateParser,
  MONTH_NAMES,
} from "common247";

/**
 * Detects MIME type from URL or filename.
 *
 * @param url - Media download URL or filename.
 * @returns Detected MIME type string.
 */
export function getMediaTypeFromUrl(url?: string | null): string {
  if (!url || typeof url !== "string") {
    return "image/jpeg";
  }
  const cleanUrl = url.split("?")[0].toLowerCase();
  if (
    cleanUrl.endsWith(".mp3") ||
    cleanUrl.endsWith(".wav") ||
    cleanUrl.endsWith(".m4a") ||
    cleanUrl.endsWith(".aac") ||
    cleanUrl.endsWith(".ogg")
  ) {
    return "audio/mpeg";
  }
  if (
    cleanUrl.endsWith(".mp4") ||
    cleanUrl.endsWith(".mov") ||
    cleanUrl.endsWith(".webm") ||
    cleanUrl.endsWith(".avi") ||
    cleanUrl.endsWith(".mkv")
  ) {
    return "video/mp4";
  }
  if (cleanUrl.endsWith(".pdf")) {
    return "application/pdf";
  }
  if (
    cleanUrl.endsWith(".doc") ||
    cleanUrl.endsWith(".docx") ||
    cleanUrl.endsWith(".txt") ||
    cleanUrl.endsWith(".rtf") ||
    cleanUrl.endsWith(".csv") ||
    cleanUrl.endsWith(".xlsx") ||
    cleanUrl.endsWith(".pptx")
  ) {
    return "application/msword";
  }
  return "image/jpeg";
}

/**
 * Extracts a readable filename from URL.
 *
 * @param url - Media download URL.
 * @param index - Fallback index.
 * @returns Extracted filename or fallback string.
 */
export function getMediaNameFromUrl(url?: string | null, index: number = 0): string {
  if (!url || typeof url !== "string") {
    return `Media ${index + 1}`;
  }
  try {
    const cleanUrl = url.split("?")[0];
    const segments = cleanUrl.split("/");
    const last = segments[segments.length - 1];
    if (last && last.trim().length > 0) {
      return decodeURIComponent(last).trim();
    }
  } catch {
    // Fallback on decode failure
  }
  return `Media ${index + 1}`;
}

/**
 * Synthesizes MediaModel items from a list of URLs when full models are not supplied.
 *
 * @param urls - Array of media URLs.
 * @param ownerId - Optional owner UID.
 * @param createdAt - Optional creation timestamp.
 * @returns Synthesized MediaModel objects.
 */
export function urlsToMediaModels(urls: string[], ownerId?: string, createdAt?: number): MediaModel[] {
  return urls
    .filter((url): url is string => Boolean(url && typeof url === "string" && url.trim().length > 0))
    .map((rawUrl, idx) => {
      const cleanUrl = rawUrl.trim();
      const sanitizedUrl = cleanUrl.includes(" ") ? encodeURI(cleanUrl) : cleanUrl;
      return {
        id: `url-media-${idx}-${sanitizedUrl.slice(-10)}`,
        downloadUrl: sanitizedUrl,
        title: getMediaNameFromUrl(cleanUrl, idx),
        ownerId: ownerId || "",
        storagePath: "",
        contentType: getMediaTypeFromUrl(cleanUrl),
        createdAt: createdAt || Date.now(),
      } as MediaModel;
    });
}
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";
import { CustomAlert } from "../ThemedAlert";
import { ThemedProgressBar } from "../ThemedProgressBar";
import { ThemedList, type ISortOption, type IGroupOption, type ICustomFilterOption } from "../ThemedList";
import { router, useFocusEffect } from "expo-router";
import { entitySync } from "@/services/sync/entity-sync";
import { MediaCard, type MediaViewMode } from "./MediaCard";
import { CapturePanel } from "@/components/CapturePanel";
import type { ICapturedGeoLocation } from "@/services/media-capture";
import { readFileAsBase64, blobToBase64, MAX_SAFE_BASE64_FILE_SIZE_BYTES } from "@/utils/file-reader";
import { useThemeColor } from "../../hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import { ThemedViewSelector } from "@/components/ThemedViewSelector";
import { trackMediaUploaded } from "@/services/analytics";
import { uploadQueue } from "@/services/upload-queue";
import {
  startMediaSendSession,
  logMediaSendMetadataStep,
  logMediaSendNetworkStart,
  logMediaSendComplete,
  logMediaSendFailure,
} from "@/services/media-telemetry";

const COLUMN_COUNT = 2;

/**
 * Formats a timestamp or ISO string into "Month Year" (e.g., "October 2026").
 * @param {number | string} [timestamp] - Timestamp value.
 * @returns {string} Formatted month and year string.
 */
export function formatMonthYear(timestamp?: number | string): string {
  if (!timestamp) return "Unknown Date";
  const dateObj = new Date(timestamp);
  if (isNaN(dateObj.getTime())) return "Unknown Date";
  return dateObj.toLocaleDateString("en-US", { year: "numeric", month: "long" });
}

/**
 * Resolves a chronological sort timestamp for a media item using its mediaDate.
 * Falls back to capturedAt or createdAt if mediaDate is absent.
 */
export function getMediaDateSortValue(m: MediaModel): number {
  const rawDate = m.date;
  if (rawDate) {
    const parsed = DateParser.parse(rawDate);
    if (parsed.from && parsed.from.year) {
      const year = parsed.from.year;
      const month = (parsed.from.month ?? 1) - 1;
      const day = parsed.from.day ?? 1;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (parsed.from.time) {
        const parts = parsed.from.time.split(":");
        hours = parseInt(parts[0], 10) || 0;
        minutes = parseInt(parts[1], 10) || 0;
        seconds = parseInt(parts[2], 10) || 0;
      }
      const d = new Date(year, month, day, hours, minutes, seconds);
      if (!isNaN(d.getTime())) {
        return d.getTime();
      }
    }
  }
  return m.metadata?.capturedAt || m.createdAt || 0;
}

/**
 * Extracts the 4-digit calendar year from a media item's mediaDate for grouping.
 * Falls back to capturedAt or createdAt.
 */
export function getMediaDateYear(m: MediaModel): string {
  const rawDate = m.date;
  if (rawDate) {
    const parsed = DateParser.parse(rawDate);
    if (parsed.from?.year) {
      return String(parsed.from.year);
    }
  }
  const fallbackTs = m.metadata?.capturedAt || m.createdAt;
  if (fallbackTs) {
    const d = new Date(fallbackTs);
    if (!isNaN(d.getTime())) {
      return String(d.getFullYear());
    }
  }
  return "Unknown Year";
}

export const MEDIA_SORT_OPTIONS: ISortOption<MediaModel>[] = [
  { key: "title", label: "Title", getValue: (m) => m.title },
  { key: "date", label: "Date", getValue: (m) => getMediaDateSortValue(m) },
  { key: "mediaDate", label: "Media Date", getValue: (m) => getMediaDateSortValue(m) },
  { key: "createdAt", label: "Upload Date", getValue: (m) => m.createdAt },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (m) => m.favouriteRating ?? 3 },
  { key: "contentType", label: "Media Type", getValue: (m) => m.contentType },
  { key: "owner", label: "Owner", getValue: (m) => m.ownerDisplayName || "" },
];

export const MEDIA_TYPE_FILTER: ICustomFilterOption<MediaModel> = {
  key: "mediaType",
  label: "Type:",
  defaultValue: "all",
  options: [
    { label: "All Types", value: "all" },
    { label: "Photos & Images", value: "image" },
    { label: "Videos", value: "video" },
    { label: "Audio Recordings", value: "audio" },
    { label: "Documents", value: "document" },
    { label: "PDF Files", value: "pdf" },
    { label: "Other", value: "other" },
  ],
  predicate: (item, val) => {
    if (val === "all") return true;
    const detected = item.mediaType || getMediaType(item.contentType, item.title);
    return detected === val;
  },
};

export const MEDIA_FAVOURITE_FILTER: ICustomFilterOption<MediaModel> = {
  key: "favouriteRating",
  label: "Rating:",
  defaultValue: "all",
  options: [
    { label: "All Ratings", value: "all" },
    { label: "★ 5 Stars", value: "5" },
    { label: "★ 4 Stars", value: "4" },
    { label: "★ 3 Stars", value: "3" },
    { label: "★ 2 Stars", value: "2" },
    { label: "★ 1 Star", value: "1" },
    { label: "4+ Stars", value: "4_plus" },
    { label: "3+ Stars", value: "3_plus" },
  ],
  predicate: (item, val) => {
    const rate = item.favouriteRating ?? 3;
    if (val === "all") return true;
    if (val === "4_plus") return rate >= 4;
    if (val === "3_plus") return rate >= 3;
    return rate === Number(val);
  },
};

/**
 * Formats a favourite rating number (1-5) into a display string with star glyphs and star count.
 * Defaults undefined or null ratings to 3 stars.
 *
 * @param rating - Rating value between 1 and 5 (defaults to 3 if omitted).
 * @returns Human-readable formatted string (e.g. "★★★★★ (5 Stars)").
 */
export function formatFavouriteRatingGroup(rating?: number): string {
  const rawRate = rating ?? 3;
  const rate = Math.max(1, Math.min(5, Math.round(rawRate)));
  const stars = "★".repeat(rate);
  return `${stars} (${rate} Star${rate === 1 ? "" : "s"})`;
}

/**
 * Group option for categorizing media items by their system-classified MediaCategory.
 */
export const MEDIA_CATEGORY_GROUP_OPTION: IGroupOption<MediaModel> = {
  key: "mediaCategory",
  label: "Media Category",
  getGroupValue: (m) => {
    const cat = m.mediaCategory || determineMediaCategory({
      contentType: m.contentType,
      fileName: m.title,
      mediaType: m.mediaType,
      metadata: m.metadata,
    });
    return formatMediaCategory(cat);
  },
};

/**
 * Group option for categorizing media items by their favourite rating.
 */
export const MEDIA_FAVOURITE_GROUP_OPTION: IGroupOption<MediaModel> = {
  key: "favouriteRating",
  label: "Favourite Rating",
  getGroupValue: (m) => formatFavouriteRatingGroup(m.favouriteRating),
};

export const MEDIA_GROUP_OPTIONS: IGroupOption<MediaModel>[] = [
  MEDIA_CATEGORY_GROUP_OPTION,
  MEDIA_FAVOURITE_GROUP_OPTION,
  {
    key: "mediaDateYear",
    label: "Media Date Year",
    getGroupValue: (m) => getMediaDateYear(m),
  },
  {
    key: "mediaDateMonth",
    label: "Media Date Month",
    getGroupValue: (m) => {
      const rawDate = m.date;
      if (rawDate) {
        const parsed = DateParser.parse(rawDate);
        if (parsed.from?.year) {
          const monthIdx = (parsed.from.month ?? 1) - 1;
          const monthName = MONTH_NAMES[monthIdx] || "January";
          return `${monthName} ${parsed.from.year}`;
        }
      }
      const ts = m.metadata?.capturedAt || m.createdAt;
      return formatMonthYear(ts);
    },
  },
  {
    key: "uploadMonth",
    label: "Upload Date Month",
    getGroupValue: (m) => formatMonthYear(m.createdAt),
  },
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (m) => m.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "type",
    label: "Media Type",
    getGroupValue: (m) => {
      const type = m.mediaType || getMediaType(m.contentType, m.title);
      if (type === "image" || m.contentType?.startsWith("image/")) return "Photos & Images";
      if (type === "video" || m.contentType?.startsWith("video/")) return "Videos";
      if (type === "audio" || m.contentType?.startsWith("audio/")) return "Audio Recordings";
      if (type === "pdf" || isPdfMedia(m.contentType, m.title)) return "PDF Files";
      if (type === "document" || isDocumentMedia(m.contentType, m.title)) return "Documents";
      return "Other Files";
    },
  },
  {
    key: "role",
    label: "Permission Role",
    getGroupValue: (m) => (m.userRole === "owner" ? "My Uploads (Owner)" : `Shared with me (${m.userRole || "Viewer"})`),
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (m) => {
      const tags = Object.values(m.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

/**
 * Props for the IMediaGallery component.
 */
export interface IMediaGalleryProps {
  /** Optional pre-loaded media list (e.g. when showing media in a Memory) */
  data?: MediaModel[];
  /** Optional media URLs to display (auto-synthesizes MediaModel if data is omitted) */
  mediaUrls?: string[];
  /** Layout mode: "horizontal" for compact scroll strip (default for card embedding), "gallery" for full screen */
  layout?: "gallery" | "horizontal";
  /** Whether the gallery is embedded within another component/card (e.g. MemoryCard) */
  embedded?: boolean;
  /** Whether to render the capture and upload panel (defaults to false when embedded, true otherwise) */
  showCapturePanel?: boolean;
  /** Override for view mode (details, compact, small, medium, large) */
  viewMode?: MediaViewMode;
  /** Optional callback when a media item is pressed */
  onMediaPress?: (media: MediaModel) => void;
  /** Optional owner ID for synthesized media models */
  ownerId?: string;
  /** Optional creation timestamp for synthesized media models */
  createdAt?: number;
  /** Optional compact styling flag */
  compact?: boolean;
  /** Optional container style */
  style?: StyleProp<ViewStyle>;
  /** Optional empty state text */
  emptyText?: string;
  onSelect?: (media: MediaModel[]) => void;
  selectionMode?: boolean;
  initialSelectedIds?: string[];
  selectedIds?: string[];
  onLoadingChange?: (loading: boolean) => void;
  showProgressBar?: boolean;
  useThemedList?: boolean;
  toolbarRightComponent?: React.ReactNode;
  addButton?:
  | {
    title: string;
    onPress: () => void;
    variant?: "primary" | "secondary";
    testID?: string;
  }
  | React.ReactNode;
  /** Optional callback fired whenever the active media list is refreshed or updated */
  onMediaListChange?: (media: MediaModel[]) => void;
  /** Whether to suppress automatic Memory creation on media upload (defaults to true) */
  skipAutoMemory?: boolean;
  /** Optional filter to restrict media types (e.g. ['image']). When provided, only matching media is displayed. */
  allowedMediaTypes?: string[];
  /** Whether to show the segmented view mode selector (defaults to true) */
  showViewSelector?: boolean;
  /** Optional callback triggered on pull-to-refresh */
  onRefresh?: () => void | Promise<void>;
}

const VIEW_MODES: { key: MediaViewMode; icon: keyof typeof Ionicons.glyphMap; title: string }[] = [
  { key: "details", icon: "list-outline", title: "Details (Rows)" },
  { key: "compact", icon: "reorder-four-outline", title: "Compact (14 Columns)" },
  { key: "small", icon: "grid-outline", title: "Small (50x50)" },
  { key: "medium", icon: "apps-outline", title: "Medium (200x200)" },
  { key: "large", icon: "square-outline", title: "Large (500x500)" },
];

/**
 * Media gallery component displaying uploaded media grouped by owner with drag-and-drop upload and unified busy states.
 * Reusable for standalone galleries and embedding within entity cards (e.g. MemoryCard).
 */
export function MediaGallery({
  data,
  mediaUrls,
  layout = "gallery",
  embedded = false,
  showCapturePanel,
  viewMode: controlledViewMode,
  onMediaPress,
  ownerId,
  createdAt,
  compact = false,
  style,
  emptyText,
  onSelect,
  selectionMode = false,
  initialSelectedIds = [],
  selectedIds: controlledSelectedIds,
  onLoadingChange,
  showProgressBar = selectionMode,
  useThemedList = !selectionMode,
  toolbarRightComponent,
  addButton,
  onMediaListChange,
  skipAutoMemory = true,
  allowedMediaTypes,
  showViewSelector = true,
  onRefresh,
}: IMediaGalleryProps) {
  const services = useFirebase();
  const { user } = useAuth();
  const [mediaList, setMediaList] = useState<MediaModel[]>([]);
  const [internalViewMode, setInternalViewMode] = useState<MediaViewMode>("medium");
  const viewMode = controlledViewMode || internalViewMode;
  const isControlledData = Boolean(data !== undefined || (mediaUrls && mediaUrls.length > 0));

  const effectiveMediaList = useMemo(() => {
    let list: MediaModel[] = [];
    if (data !== undefined) {
      list = data;
    } else if (mediaUrls && mediaUrls.length > 0) {
      list = urlsToMediaModels(mediaUrls, ownerId, createdAt);
    } else {
      list = mediaList;
    }
    if (allowedMediaTypes && allowedMediaTypes.length > 0) {
      return list.filter((item) => {
        const detected = item.mediaType || getMediaType(item.contentType, item.title);
        return (
          allowedMediaTypes.includes(detected) ||
          (allowedMediaTypes.includes("image") && (item.contentType?.startsWith("image/") || detected === "image"))
        );
      });
    }
    return list;
  }, [data, mediaUrls, ownerId, createdAt, mediaList, allowedMediaTypes]);

  const shouldShowCapturePanel = showCapturePanel ?? !embedded;
  const isAndroid =
    Platform.OS === "android" ||
    (Platform.OS === "web" && typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || ""));

  const [loading, setLoading] = useState(!isControlledData);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState<string>("Uploading...");
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(initialSelectedIds);
  const selectedIds = controlledSelectedIds !== undefined ? controlledSelectedIds : internalSelectedIds;

  useEffect(() => {
    AsyncStorage.getItem("media_view_preference")
      .then((saved) => {
        if (saved === "details" || saved === "small" || saved === "medium" || saved === "large") {
          setInternalViewMode(saved);
        }
      })
      .catch(() => { });
  }, []);

  const handleViewModeChange = (mode: MediaViewMode) => {
    setInternalViewMode(mode);
    AsyncStorage.setItem("media_view_preference", mode).catch(() => { });
  };

  const renderViewSelector = () => (
    <ThemedViewSelector<MediaViewMode>
      activeMode={viewMode}
      onModeChange={handleViewModeChange}
      options={VIEW_MODES}
    />
  );

  const mountTimeRef = useRef(performance.now());
  const loggedRef = useRef(false);

  useEffect(() => {
    onLoadingChange?.(loading);
    if (loading === false && !loggedRef.current) {
      const duration = performance.now() - mountTimeRef.current;
      console.info(`[Page Load Metric] MediaGallery loaded in ${duration.toFixed(2)}ms`);
      loggedRef.current = true;
    }
  }, [loading, onLoadingChange]);


  const [isCtrlPressed, setIsCtrlPressed] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        setIsCtrlPressed(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        setIsCtrlPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Listen for items completed by the persistent uploadQueue (e.g. from background resumption)
  useEffect(() => {
    if (Platform.OS === "web") return;
    const unsubscribe = uploadQueue.subscribe((event) => {
      if (event.type === "item_completed" && event.media) {
        setMediaList((prev) => {
          if (prev.some((m) => m.id === event.media.id)) return prev;
          const next = [event.media, ...prev];
          onMediaListChange?.(next);
          return next;
        });
      }
    });
    return unsubscribe;
  }, [onMediaListChange]);

  const headerBgColor = useThemeColor({}, "buttonSecondary");

  // Sharing state
  const [sharingMedia, setSharingMedia] = useState<MediaModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [sharesByEntityId, setSharesByEntityId] = useState<Record<string, ShareModel[]>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const handleToggleInlineSharing = (media: MediaModel) => {
    if (sharingMedia?.id === media.id) {
      setSharingMedia(null);
    } else {
      setSharingMedia(media);
      fetchSharingData(media.id!);
    }
  };

  const fetchMedia = async () => {
    if (!services || !user) return;
    try {
      const res = await getMyMedia(services, {});
      if (res.status) {
        setMediaList(res.media);
        onMediaListChange?.(res.media);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to load media");
      }
    } catch (error: any) {
      console.error("Fetch media error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSharingData = async (mediaId: string) => {
    if (!services) return;
    setLoadingShares(true);
    try {
      const [sharesRes, contactsRes, groupsRes] = await Promise.all([
        getSharesForSource(services, { sourceId: mediaId, sourceType: "media" }),
        getMyContacts(services, { includeInactive: false }),
        getMyGroups(services, {}),
      ]);
      if (sharesRes.status) {
        setShares(sharesRes.shares);
        setSharesByEntityId((prev) => ({ ...prev, [mediaId]: sharesRes.shares }));
        const updatedSummaries: ShareSummary[] = sharesRes.shares.map((s) => ({
          id: s.targetId,
          displayName: s.targetDisplayName || "Unknown",
          type: s.targetType,
          role: s.role,
          targetUserId: s.targetUserId,
        }));
        setMediaList((prev) =>
          prev.map((m) => (m.id === mediaId ? { ...m, sharedWith: updatedSummaries } : m))
        );
      }
      if (contactsRes.status) setContacts(contactsRes.contacts);
      if (groupsRes.status) setGroups(groupsRes.groups);
    } catch (error) {
      console.error("Error fetching sharing data:", error);
    } finally {
      setLoadingShares(false);
    }
  };

  useEffect(() => {
    if (isControlledData) {
      setLoading(false);
    } else {
      fetchMedia();
    }
  }, [isControlledData, services, user]);

  useFocusEffect(
    useCallback(() => {
      if (!isControlledData && services && user) {
        fetchMedia();
      }
    }, [isControlledData, services, user])
  );

  useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "media") {
        if (event.action === "update" && event.data) {
          setMediaList((prev) => {
            const next = prev.map((m) => (m.id === event.id ? { ...m, ...event.data } : m));
            onMediaListChange?.(next);
            return next;
          });
        } else if (event.action === "delete") {
          setMediaList((prev) => {
            const next = prev.filter((m) => m.id !== event.id);
            onMediaListChange?.(next);
            return next;
          });
        } else if (!isControlledData) {
          fetchMedia();
        }
      }
    });
    return unsubscribe;
  }, [isControlledData, services, user]);

  /**
   * Uploads multiple media files in sequence, tracking per-item progress,
   * extracting offline metadata, and reporting batch upload results.
   * Supports both web File instances and native Android capture asset objects.
   * @param files - Array of files dropped, selected, or captured.
   * @param geoLocation - Optional high-accuracy GPS coordinates acquired during capture.
   */
  const handleUploadFiles = async (files: (File | any)[], geoLocation?: ICapturedGeoLocation | null) => {
    if (!services || !user || files.length === 0) return;

    const validFiles: any[] = [];
    const invalidFileNames: string[] = [];

    for (const f of files) {
      const type = (f.type || "").toLowerCase();
      const isZip =
        type.includes("zip") ||
        type.includes("compressed") ||
        type.includes("tar") ||
        type.includes("gzip") ||
        type.includes("7z") ||
        type.includes("rar") ||
        /\.(zip|tar|gz|gzip|7z|rar|bz2|xz)$/i.test(f.name || "");
      if (isZip) {
        invalidFileNames.push(f.name || "archive");
        continue;
      }
      const detected = getMediaType(type, f.name);
      const isValid =
        type.startsWith("image/") ||
        type.startsWith("audio/") ||
        type.startsWith("video/") ||
        detected === "image" ||
        detected === "video" ||
        detected === "audio" ||
        detected === "document" ||
        detected === "pdf";
      const matchesAllowed =
        !allowedMediaTypes ||
        allowedMediaTypes.length === 0 ||
        allowedMediaTypes.includes(detected) ||
        (allowedMediaTypes.includes("image") && (type.startsWith("image/") || detected === "image"));
      if (isValid && matchesAllowed) {
        validFiles.push(f);
      } else {
        invalidFileNames.push(f.name || "unknown");
      }
    }

    if (validFiles.length === 0) {
      CustomAlert.alert(
        "Invalid File",
        "Please select image, video, audio, document, or PDF files. Compressed files and ZIP archives are not supported."
      );
      return;
    }

    setUploading(true);
    setUploadStatusText(validFiles.length > 1 ? `Uploading 1 of ${validFiles.length}...` : "Uploading...");
    if (Platform.OS !== "web") {
      activateKeepAwakeAsync("MediaGalleryUpload").catch(() => {});
    }

    const successfulMedia: MediaModel[] = [];
    const duplicateMedia: { fileName: string; existingMediaId?: string; existingMediaUrl?: string }[] = [];
    const failedMedia: { fileName: string; error: string }[] = [];

    const toBase64 = async (file: any): Promise<string> => {
      if (file.base64) {
        return file.base64;
      }
      const rawBlob = file instanceof Blob ? file : (file?.file instanceof Blob ? file.file : null);
      if (rawBlob && typeof FileReader !== "undefined") {
        if (typeof rawBlob.size === "number" && rawBlob.size > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
          const sizeMb = (rawBlob.size / (1024 * 1024)).toFixed(1);
          throw new Error(
            `File is too large for in-memory processing (${sizeMb} MB). Files larger than 30 MB exceed device memory limits.`
          );
        }
        return await blobToBase64(rawBlob);
      }
      if (file.uri) {
        return await readFileAsBase64(file.uri);
      }
      if (typeof FileReader !== "undefined" && typeof file?.slice === "function") {
        return await blobToBase64(file);
      }
      throw new Error(`Unable to read file data for ${file.name || "selected file"}`);
    };

    const sendSource = geoLocation ? "camera_capture" : "file_picker";

    try {
      if (Platform.OS !== "web") {
        // Native (Android & iOS): Enqueue into persistent disk-sandboxed uploadQueue for sleep/suspend resilience
        const queueItems = await Promise.all(
          validFiles.map(async (file) => {
            let extractedMetadata: any = {};
            try {
              extractedMetadata = (await extractMediaMetadata(file, file.type)) || {};
            } catch {
              extractedMetadata = {};
            }
            if (geoLocation) {
              extractedMetadata.gpsLatitude = geoLocation.latitude;
              extractedMetadata.gpsLongitude = geoLocation.longitude;
              extractedMetadata.gpsLocationLabel = geoLocation.label;
            }
            return {
              uri: file.uri || file.path || "",
              name: file.name || "Untitled",
              type: file.type || "image/jpeg",
              size: file.size || 0,
              metadata: {
                ...extractedMetadata,
                href: extractedMetadata.href || file.href || file.url || file.originalUrl || file.uri || file.name,
              },
              location: geoLocation
                ? {
                    latitude: geoLocation.latitude,
                    longitude: geoLocation.longitude,
                    text: geoLocation.label,
                  }
                : undefined,
              skipAutoMemory: skipAutoMemory ?? true,
            };
          })
        );

        const unsubscribe = uploadQueue.subscribe((event) => {
          if (event.type === "progress" && event.currentIndex && event.totalItems) {
            setUploadStatusText(`Uploading ${event.currentIndex} of ${event.totalItems}...`);
          } else if (event.type === "item_completed" && event.media) {
            successfulMedia.push(event.media);
            trackMediaUploaded(event.media.id || "batch", 1);
            setMediaList((prev) => {
              if (prev.some((m) => m.id === event.media.id)) return prev;
              const next = [event.media, ...prev];
              onMediaListChange?.(next);
              return next;
            });
          } else if (event.type === "item_duplicate") {
            duplicateMedia.push({
              fileName: event.item?.name || "duplicate",
              existingMediaId: event.existingMediaId,
              existingMediaUrl: event.existingMediaUrl,
            });
          } else if (event.type === "item_failed") {
            failedMedia.push({
              fileName: event.item?.name || "file",
              error: event.lastError || "Upload failed",
            });
          }
        });

        try {
          await uploadQueue.enqueueFiles(queueItems);
          await uploadQueue.processQueue(services);
        } finally {
          unsubscribe();
        }
      } else {
        // Web: In-memory browser file handling
        for (let i = 0; i < validFiles.length; i++) {
          const file = validFiles[i];
          if (validFiles.length > 1) {
            setUploadStatusText(`Uploading ${i + 1} of ${validFiles.length}...`);
          }

          const session = startMediaSendSession({
            source: sendSource,
            totalItems: validFiles.length,
            itemIndex: i + 1,
            fileName: file.name,
            mimeType: file.type,
            fileSizeBytes: file.size,
          });

          try {
            const base64 = await toBase64(file);
            const [metadataResult] = await Promise.allSettled([extractMediaMetadata(file, file.type)]);
            const extractedMetadata = metadataResult.status === "fulfilled" ? (metadataResult.value || {}) : {};

            if (geoLocation) {
              extractedMetadata.gpsLatitude = geoLocation.latitude;
              extractedMetadata.gpsLongitude = geoLocation.longitude;
              extractedMetadata.gpsLocationLabel = geoLocation.label;
            }

            logMediaSendMetadataStep(session, {
              hasGps: Boolean(geoLocation || extractedMetadata.gpsLatitude != null),
              hasExif: Boolean(extractedMetadata.extraMetadata || (extractedMetadata as any)?.hasExif),
              locationLabel: geoLocation?.label || extractedMetadata.gpsLocationLabel,
            });

            const payloadBytes = base64.length ? Math.round((base64.length * 3) / 4) : file.size;
            logMediaSendNetworkStart(session, payloadBytes);

            const res = await createMedia(services, {
              base64,
              title: file.name,
              contentType: file.type,
              href: extractedMetadata.href || (file as any)?.href || (file as any)?.url || (file as any)?.originalUrl || (file as any)?.uri || file.name,
              skipAutoMemory: skipAutoMemory ?? true,
              metadata: {
                ...extractedMetadata,
                href: extractedMetadata.href || (file as any)?.href || (file as any)?.url || (file as any)?.originalUrl || (file as any)?.uri || file.name,
              },
              location: geoLocation
                ? {
                    latitude: geoLocation.latitude,
                    longitude: geoLocation.longitude,
                    text: geoLocation.label,
                  }
                : undefined,
            });

            if (res.status && res.media) {
              successfulMedia.push(res.media);
              logMediaSendComplete(session, {
                mediaId: res.media.id,
                isDuplicate: false,
                serverDiagnostics: res.serverDiagnostics,
              });
            } else if (res.isDuplicate || res.error?.includes("already been uploaded")) {
              duplicateMedia.push({
                fileName: file.name,
                existingMediaId: res.existingMediaId,
                existingMediaUrl: res.existingMediaUrl,
              });
              logMediaSendComplete(session, {
                mediaId: res.existingMediaId,
                isDuplicate: true,
                serverDiagnostics: res.serverDiagnostics,
              });
            } else {
              failedMedia.push({
                fileName: file.name,
                error: res.error || "Unknown error",
              });
              logMediaSendFailure(session, {
                step: "server",
                errorMessage: res.error || "Upload rejected",
              });
            }
          } catch (fileErr: any) {
            logMediaSendFailure(session, {
              step: "upload",
              errorMessage: fileErr.message || "Upload exception",
            });
            failedMedia.push({
              fileName: file.name,
              error: fileErr.message || "Upload exception",
            });
          }
        }
      }

      // Prepend all successfully uploaded media at once (if not already added)
      if (successfulMedia.length > 0) {
        trackMediaUploaded(successfulMedia[0]?.id || "batch", successfulMedia.length);
        setMediaList((prev) => {
          const toAdd = successfulMedia.filter((sm) => !prev.some((p) => p.id === sm.id));
          if (toAdd.length === 0) return prev;
          const next = [...toAdd, ...prev];
          onMediaListChange?.(next);
          return next;
        });
      }

      // Display feedback based on single or batch upload
      if (validFiles.length === 1) {
        if (duplicateMedia.length > 0) {
          const dup = duplicateMedia[0];
          const mediaId = dup.existingMediaId;
          const mediaUrl = dup.existingMediaUrl || (mediaId ? `/my-media/${mediaId}` : "");
          CustomAlert.alert(
            "Upload Warning",
            "This has already been uploaded.",
            [
              ...(mediaId ? [{ text: "View Media", onPress: () => router.push(`/my-media/${mediaId}`) }] : []),
              { text: "Dismiss", style: "cancel" as const },
            ],
            mediaUrl && mediaId
              ? {
                  link: {
                    label: `Open ${mediaUrl}`,
                    url: mediaUrl,
                    onPress: () => router.push(`/my-media/${mediaId}`),
                  },
                }
              : undefined
          );
        } else if (failedMedia.length > 0) {
          const failed = failedMedia[0];
          const isInterrupted =
            failed.error.toLowerCase().includes("interrupted") ||
            failed.error.toLowerCase().includes("sleep") ||
            failed.error.toLowerCase().includes("connection");
          CustomAlert.alert(
            isInterrupted ? "Upload Interrupted" : "Upload Failed",
            failed.error,
            isInterrupted
              ? [
                  { text: "Retry", onPress: () => handleFilesSelected(validFiles) },
                  { text: "Dismiss", style: "cancel" as const },
                ]
              : undefined
          );
        }
      } else {
        const summaryParts: string[] = [];
        if (successfulMedia.length > 0) {
          summaryParts.push(`Safely uploaded ${successfulMedia.length} of ${validFiles.length} item(s).`);
        }
        if (duplicateMedia.length > 0) {
          summaryParts.push(
            `${duplicateMedia.length} item(s) were already safely uploaded previously:\n` +
              duplicateMedia.map((d) => `• ${d.fileName}`).join("\n")
          );
        }
        if (failedMedia.length > 0) {
          summaryParts.push(
            `${failedMedia.length} item(s) failed:\n` +
              failedMedia.map((f) => `• ${f.fileName} (${f.error})`).join("\n")
          );
        }
        if (invalidFileNames.length > 0) {
          summaryParts.push(
            `Skipped ${invalidFileNames.length} non-media file(s):\n` +
              invalidFileNames.map((n) => `• ${n}`).join("\n")
          );
        }

        const hasInterrupted = failedMedia.some(
          (f) =>
            f.error.toLowerCase().includes("interrupted") ||
            f.error.toLowerCase().includes("sleep") ||
            f.error.toLowerCase().includes("connection")
        );
        const alertTitle = failedMedia.length > 0 ? (hasInterrupted ? "Upload Interrupted" : "Upload Summary") : "Upload Complete";
        const alertButtons = hasInterrupted
          ? [
              {
                text: "Retry Failed",
                onPress: () => {
                  const failedNames = new Set(failedMedia.map((f) => f.fileName));
                  const toRetry = validFiles.filter((f: any) => failedNames.has(f.name));
                  if (toRetry.length > 0) {
                    handleUploadFiles(toRetry);
                  }
                },
              },
              { text: "Dismiss", style: "cancel" as const },
            ]
          : undefined;

        CustomAlert.alert(alertTitle, summaryParts.join("\n\n"), alertButtons);
      }
    } catch (globalErr: any) {
      console.error("Batch upload error:", globalErr);
      CustomAlert.alert("Upload Error", globalErr.message || "An unexpected error occurred during upload.");
    } finally {
      if (Platform.OS !== "web") {
        deactivateKeepAwake("MediaGalleryUpload");
      }
      setUploading(false);
      setUploadStatusText("Uploading...");
    }
  };

  const handleItemLongPress = (media: MediaModel) => {
    const newSelectedIds = selectedIds.includes(media.id!)
      ? selectedIds
      : [...selectedIds, media.id!];
    if (controlledSelectedIds === undefined) {
      setInternalSelectedIds(newSelectedIds);
    }
    if (onSelect) {
      onSelect(effectiveMediaList.filter((m) => newSelectedIds.includes(m.id!)));
    }
  };

  const handleItemContextMenu = (media: MediaModel, _event?: any) => {
    handleItemLongPress(media);
  };

  const toggleSelection = (media: MediaModel, event?: any) => {
    const isCtrlOrCmd = Boolean(
      event?.nativeEvent?.ctrlKey ||
      event?.nativeEvent?.metaKey ||
      event?.ctrlKey ||
      event?.metaKey ||
      isCtrlPressed
    );

    if (!selectionMode && !isCtrlOrCmd && selectedIds.length === 0) {
      router.push(`/my-media/${media.id}`);
      return;
    }

    const isSelected = selectedIds.includes(media.id!);
    const newSelectedIds = isSelected ? selectedIds.filter((id) => id !== media.id!) : [...selectedIds, media.id!];

    if (controlledSelectedIds === undefined) {
      setInternalSelectedIds(newSelectedIds);
    }

    if (onSelect) {
      onSelect(effectiveMediaList.filter((m) => newSelectedIds.includes(m.id!)));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        await fetchMedia();
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleShare = async (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => {
    if (!services || !sharingMedia) return;
    try {
      const res = await share(services, {
        sourceId: sharingMedia.id!,
        sourceType: "media",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchSharingData(sharingMedia.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to share.");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingMedia) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchSharingData(sharingMedia.id!);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share.");
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!services) return;
    const targetMedia = mediaList.find((m) => m.id === mediaId);
    await confirmEntityDeletion({
      services,
      entityType: "media",
      entityId: mediaId,
      entityName: targetMedia?.title,
      onProceedDelete: (cascadeUnlink) => deleteMedia(services, { mediaId, cascadeUnlink }),
      onSuccess: () => {
        setMediaList((prev) => {
          const next = prev.filter((m) => m.id !== mediaId);
          onMediaListChange?.(next);
          return next;
        });
      },
    });
  };

  const handleNativeShare = async (media: MediaModel) => {
    try {
      const title = media.title || "Media";
      const shareMessage = `${title}: ${media.downloadUrl}`;
      await Share.share({
        title,
        message: shareMessage,
        url: media.downloadUrl,
      });
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "Failed to share media.");
    }
  };

  const renderMediaItem = ({ item, isHovered }: { item: MediaModel; isHovered?: boolean }) => {
    const canEdit = item.userRole === "owner" || item.userRole === "editor";
    const canNativeShare = Platform.OS !== "web" || (typeof navigator !== "undefined" && !!navigator.share);
    const isExpanded = sharingMedia?.id === item.id;
    return (
      <MediaCard
        media={item}
        currentUserId={services?.auth?.currentUser?.uid}
        viewMode={viewMode}
        isHovered={isHovered}
        onPress={(event) => {
          if (onMediaPress) {
            onMediaPress(item);
            return;
          }
          toggleSelection(item, event);
        }}
        onLongPress={() => handleItemLongPress(item)}
        onContextMenu={(e) => handleItemContextMenu(item, e)}
        isSelected={selectedIds.includes(item.id!)}
        selectionMode={selectionMode || selectedIds.length > 0}
        canEdit={canEdit}
        onEditPress={() => router.push(`/my-media/${item.id}`)}
        onShare={() => handleToggleInlineSharing(item)}
        isSharingExpanded={isExpanded}
        onToggleSharing={() => handleToggleInlineSharing(item)}
        shares={sharesByEntityId[item.id!] ?? (isExpanded ? shares : undefined)}
        availableContacts={contacts}
        availableGroups={groups}
        isLoadingShares={isExpanded && loadingShares}
        onShareTarget={handleShare}
        onUnshare={handleUnshare}
        onNativeShare={canNativeShare ? () => handleNativeShare(item) : undefined}
        onDelete={() => handleDeleteMedia(item.id!)}
      />
    );
  };

  const groupedMedia = effectiveMediaList.reduce(
    (acc, m) => {
      const owner = m.ownerDisplayName || "Unknown owner";
      if (!acc[owner]) acc[owner] = [];
      acc[owner].push(m);
      return acc;
    },
    {} as Record<string, MediaModel[]>,
  );

  const sections = Object.keys(groupedMedia)
    .sort()
    .map((owner) => {
      const items = groupedMedia[owner];
      const rows = [];
      const cols = viewMode === "large" ? 1 : COLUMN_COUNT;
      for (let i = 0; i < items.length; i += cols) {
        rows.push(items.slice(i, i + cols));
      }
      return {
        title: owner,
        data: rows,
      };
    });

  const renderRow = ({ item: row }: { item: MediaModel[] }) => {
    const cols = viewMode === "large" ? 1 : COLUMN_COUNT;
    return (
      <View style={styles.row}>
        {row.map((m) => (
          <View key={m.id} style={styles.rowItem}>
            {renderMediaItem({ item: m })}
          </View>
        ))}
        {row.length < cols && <View style={{ flex: 1, margin: 4 }} />}
      </View>
    );
  };

  const renderSectionHeader = ({ section: { title } }: { section: { title: string } }) => (
    <ThemedView style={[styles.sectionHeader, { backgroundColor: headerBgColor }]}>
      <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
        {title}
      </ThemedText>
    </ThemedView>
  );

  if (layout === "horizontal") {
    if (effectiveMediaList.length === 0) {
      if (!emptyText) return null;
      return (
        <View style={[styles.emptyContainer, style]}>
          <ThemedText style={styles.emptyText}>{emptyText}</ThemedText>
        </View>
      );
    }

    const itemWrapperStyle =
      viewMode === "compact"
        ? styles.horizontalCompactWrapper
        : viewMode === "small"
        ? styles.horizontalSmallWrapper
        : viewMode === "large"
        ? styles.horizontalLargeWrapper
        : styles.horizontalMediumWrapper;

    return (
      <View style={[styles.horizontalWrapper, style]}>
        <FlatList
          data={effectiveMediaList}
          keyExtractor={(item, index) => item.id || `media-${index}`}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.horizontalScrollContent}
          initialNumToRender={6}
          maxToRenderPerBatch={6}
          windowSize={5}
          renderItem={({ item }) => (
            <View style={[styles.horizontalItemWrapper, itemWrapperStyle]}>
              {renderMediaItem({ item })}
            </View>
          )}
        />
      </View>
    );
  }

  return (
    <ThemedView style={[styles.container, style]}>
      {/* Shared Capture and Drop Panel */}
      {shouldShowCapturePanel && (
        <CapturePanel
          onUploadFiles={handleUploadFiles}
          uploading={uploading}
          uploadStatusText={uploadStatusText}
          compact={compact || selectionMode}
        />
      )}

      {useThemedList ? (
        <ThemedList<MediaModel>
          data={effectiveMediaList}
          entityType="media"
          sortOptions={MEDIA_SORT_OPTIONS}
          defaultSortKey="date"
          defaultSortDirection="desc"
          customFilters={[MEDIA_TYPE_FILTER, MEDIA_FAVOURITE_FILTER]}
          groupOptions={MEDIA_GROUP_OPTIONS}
          persistenceKey="my-media"
          pageSize={24}
          filterKeys={["title", "contentType", "description", "ownerDisplayName"]}
          filterPlaceholder="Search media..."
          loading={loading}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          emptyText={emptyText || (isAndroid ? "No media found. Upload some media using the capture buttons above!" : "No media found. Upload some media by dragging or capturing above!")}
          viewMode={viewMode}
          onViewModeChange={(mode) => handleViewModeChange(mode as MediaViewMode)}
          showViewSelector={showViewSelector}
          toolbarRightComponent={toolbarRightComponent}
          addButton={addButton}
          headerComponent={null}
          onItemLongPress={handleItemLongPress}
          onItemContextMenu={handleItemContextMenu}
          renderItem={(item, _index, isHovered) => (
            <View style={{ width: (viewMode === "compact" || viewMode === "chip") ? "auto" : "100%", marginBottom: viewMode === "details" ? 1 : viewMode === "small" ? 2 : viewMode === "large" ? 4 : (viewMode === "compact" || viewMode === "chip") ? 0 : 3 }}>
              {renderMediaItem({ item, isHovered })}
            </View>
          )}
        />
      ) : (
        <>
          {showViewSelector && (
            <View style={{ flexDirection: "row", justifyContent: "flex-end", padding: 8 }}>
              {renderViewSelector()}
            </View>
          )}
          {showProgressBar && <ThemedProgressBar loading={loading} />}
          {!loading && effectiveMediaList.length === 0 ? (
            <View style={styles.center}>
              <ThemedText type="subtitle">No media found</ThemedText>
              <ThemedText style={styles.emptyText}>{emptyText || (isAndroid ? "Upload some media using the buttons above!" : "Upload some media by dragging them here!")}</ThemedText>
            </View>
          ) : !loading ? (
            <SectionList
              sections={sections}
              keyExtractor={(item, index) => item[0]?.id || index.toString()}
              renderItem={renderRow}
              renderSectionHeader={renderSectionHeader}
              contentContainerStyle={styles.listContent}
              onRefresh={handleRefresh}
              refreshing={Boolean(refreshing)}
              stickySectionHeadersEnabled={false}
            />
          ) : null}
        </>
      )}
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  listContent: {
    padding: 8,
  },
  emptyContainer: {
    paddingVertical: 8,
  },
  emptyText: {
    textAlign: "center",
    marginTop: 10,
    opacity: 0.6,
  },
  row: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  rowItem: {
    flex: 1,
    margin: 4,
  },
  sectionHeader: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#f0f0f0",
    borderRadius: 8,
    marginTop: 16,
    marginBottom: 8,
  },
  sectionTitle: {
    color: "#666",
    fontSize: 14,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  toolbarRightGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  viewSelectorContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    borderWidth: 1,
    padding: 2,
    gap: 2,
  },
  viewModeBtn: {
    paddingHorizontal: 7,
    paddingVertical: 4,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  viewModeBtnActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2,
  },
  horizontalWrapper: {
    marginTop: 6,
    marginBottom: 4,
  },
  horizontalScrollContent: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    gap: 6,
  },
  horizontalItemWrapper: {
    justifyContent: "center",
    alignItems: "center",
  },
  horizontalCompactWrapper: {
    width: 68,
    height: 68,
  },
  horizontalSmallWrapper: {
    width: 220,
  },
  horizontalMediumWrapper: {
    width: 140,
  },
  horizontalLargeWrapper: {
    width: 200,
  },
});

export default MediaGallery;
